// @ts-nocheck
// TODO: tighten types after migration
import React from 'react'

const VIDEO_URL_REGEX = /\.(mp4|webm|ogg|mov|m4v|m3u8)(\?|#|$)/i

// Manual converter from TipTap JSON to HTML (without @tiptap/html dependency)
function convertNodeToHTML(node, depth = 0) {
  if (!node || typeof node !== 'object') {
    return ''
  }

  const type = node.type || ''
  const attrs = node.attrs || {}
  const content = node.content || []
  const marks = node.marks || []
  const text = node.text || ''
  
  // Handle text nodes
  if (type === 'text') {
    let textContent = escapeHtml(text)
    
    // Apply marks to text
    marks.forEach(mark => {
      switch (mark.type) {
        case 'bold':
          textContent = `<strong>${textContent}</strong>`
          break
        case 'italic':
          textContent = `<em>${textContent}</em>`
          break
        case 'code':
          textContent = `<code>${textContent}</code>`
          break
        case 'underline':
          textContent = `<u>${textContent}</u>`
          break
        case 'strike':
          textContent = `<s>${textContent}</s>`
          break
        case 'link':
          const href = mark.attrs && mark.attrs.href ? mark.attrs.href : '#'
          const target = mark.attrs && mark.attrs.target ? mark.attrs.target : ''
          textContent = `<a href="${escapeHtml(href)}"${target ? ` target="${escapeHtml(target)}"` : ''}>${textContent}</a>`
          break
        default:
          break
      }
    })
    
    return textContent
  }

  // Handle block nodes
  let html = ''
  const childrenHTML = content.map(child => convertNodeToHTML(child, depth + 1)).join('')

  switch (type) {
    case 'paragraph':
      // Check if paragraph contains only an image (common in TipTap)
      if (content.length === 1 && (content[0].type === 'image' || content[0].type === 'imageWithTitle')) {
        // If paragraph only has image, return image without paragraph wrapper
        html = childrenHTML
      } else if (content.length > 0) {
        // Check if any child is an image - if so, render images outside paragraph
        const hasImage = content.some(child => child.type === 'image' || child.type === 'imageWithTitle')
        if (hasImage) {
          // Separate images from text content
          const imageHTML = content
            .filter(child => child.type === 'image' || child.type === 'imageWithTitle')
            .map(child => convertNodeToHTML(child, depth + 1))
            .join('')
          const textHTML = content
            .filter(child => child.type !== 'image' && child.type !== 'imageWithTitle')
            .map(child => convertNodeToHTML(child, depth + 1))
            .join('')
          
          if (textHTML.trim()) {
            html = `<p>${textHTML}</p>${imageHTML}`
          } else {
            html = imageHTML
          }
        } else {
          html = `<p>${childrenHTML || '<br>'}</p>`
        }
      } else {
        html = `<p>${childrenHTML || '<br>'}</p>`
      }
      break
    case 'heading':
      const level = attrs.level || 1
      // Generate ID for TOC linking
      const extractRawText = (nodes) => {
        if (!nodes || !Array.isArray(nodes)) return '';
        return nodes.map(n => n.text ? n.text : (n.content ? extractRawText(n.content) : '')).join('');
      };
      const rawText = extractRawText(content);
      const id = rawText 
        ? rawText.toLowerCase().replace(/[^\w]+/g, "-").replace(/^-+|-+$/g, "")
        : '';
      
      html = `<h${level}${id ? ` id="${id}"` : ''} class="rt-h${level}">${childrenHTML}</h${level}>`
      break
    case 'bulletList':
      html = `<ul>${childrenHTML}</ul>`
      break
    case 'orderedList':
      html = `<ol>${childrenHTML}</ol>`
      break
    case 'listItem':
      html = `<li>${childrenHTML}</li>`
      break
    case 'blockquote':
      html = `<blockquote>${childrenHTML}</blockquote>`
      break
    case 'codeBlock':
      const language = attrs.language || ''
      html = `<pre><code${language ? ` class="language-${escapeHtml(language)}"` : ''}>${escapeHtml(childrenHTML)}</code></pre>`
      break
    case 'horizontalRule':
      html = '<hr>'
      break
    case 'hardBreak':
      html = '<br>'
      break
    case 'image':
    case 'imageWithTitle':
      // Try multiple possible attribute names for image source
      // Check all possible locations for image URL
      let src = attrs.src || attrs.url || attrs.href || ''
      
      // If src is an object, try to extract URL from it
      if (!src && typeof attrs === 'object') {
        if (attrs.src && typeof attrs.src === 'object' && attrs.src.url) {
          src = attrs.src.url
        } else if (attrs.url && typeof attrs.url === 'object' && attrs.url.url) {
          src = attrs.url.url
        }
      }
      
      // If still no src, check if the entire attrs object has a url property at root level
      if (!src && node.url) {
        src = node.url
      }
      
      const alt = attrs.alt || attrs.imageTitle || attrs.title || ''
      const title = attrs.title || attrs.imageTitle || ''
      
      if (!src && process.env.NODE_ENV === 'development') {
        console.warn('⚠️ Image node found but no src/url attribute. Full node:', JSON.stringify(node, null, 2))
      }
      
      // Render video if URL points to a video asset
      if (src && VIDEO_URL_REGEX.test(src)) {
        const cleanVideoSrc = src.trim()
        if (cleanVideoSrc) {
          html = `<div class="rt rt-video"><video src="${escapeHtml(cleanVideoSrc)}" preload="metadata" playsinline></video></div>`
        } else {
          html = ''
        }
        break
      }

      // Wrap image in div with proper classes like Hygraph posts
      if (src) {
        // Ensure src is a valid URL (not empty, not just whitespace)
        const cleanSrc = src.trim()
        if (cleanSrc) {
          html = `<div class="rt rt-img"><img src="${escapeHtml(cleanSrc)}" alt="${escapeHtml(alt)}"${title ? ` title="${escapeHtml(title)}"` : ''} /></div>`
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.warn('⚠️ Image src is empty or whitespace')
          }
          html = ''
        }
      } else {
        html = ''
      }
      break
    case 'videoAsset':
      const videoSrc = attrs.src || attrs.url || attrs['data-video-asset-src'] || ''
      const videoPoster = attrs.poster || attrs['data-video-asset-poster'] || ''
      if (videoSrc && videoSrc.trim()) {
        html = `<div class="rt rt-video"><video src="${escapeHtml(videoSrc.trim())}"${videoPoster ? ` poster="${escapeHtml(videoPoster)}"` : ''} preload="metadata" playsinline></video></div>`
      } else {
        html = ''
      }
      break
    case 'blogLink':
    case 'blogEmbed':
        // Render as a special span that we can later parse and replace with the React Component
        const linkUrl = attrs.url || '';
        // For blogEmbed, the text is stored in 'title', for blogLink it is 'text'
        const linkText = attrs.text || attrs.title || '';
        html = `<span data-type="blog-link" data-url="${escapeHtml(linkUrl)}" data-text="${escapeHtml(linkText)}"></span>`
        break
    case 'table':
      html = `<div class="table-wrapper"><table class="rt rt-table"><tbody class="custom-tbody">${childrenHTML}</tbody></table></div>`
      break
    case 'tableRow':
      html = `<tr class="custom-tr">${childrenHTML}</tr>`
      break
    case 'tableHeader': {
      const colSpan = attrs.colspan ? ` colspan="${escapeHtml(String(attrs.colspan))}"` : ''
      const rowSpan = attrs.rowspan ? ` rowspan="${escapeHtml(String(attrs.rowspan))}"` : ''
      const colWidth = attrs.colwidth ? ` style="width: ${Array.isArray(attrs.colwidth) ? attrs.colwidth[0] + 'px' : attrs.colwidth + 'px'};"` : ''
      html = `<th class="custom-th"${colSpan}${rowSpan}${colWidth}>${childrenHTML}</th>`
      break
    }
    case 'tableCell': {
      const colSpan = attrs.colspan ? ` colspan="${escapeHtml(String(attrs.colspan))}"` : ''
      const rowSpan = attrs.rowspan ? ` rowspan="${escapeHtml(String(attrs.rowspan))}"` : ''
      const colWidth = attrs.colwidth ? ` style="width: ${Array.isArray(attrs.colwidth) ? attrs.colwidth[0] + 'px' : attrs.colwidth + 'px'};"` : ''
      html = `<td class="custom-td"${colSpan}${rowSpan}${colWidth}>${childrenHTML}</td>`
      break
    }
    default:
      // For unknown types, just render children
      html = childrenHTML
      break
  }

  return html
}

function escapeHtml(text) {
  if (typeof text !== 'string') {
    return ''
  }
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  }
  return text.replace(/[&<>"']/g, m => map[m])
}

// eslint-disable-next-line no-unused-vars
function findAllImageNodes(obj, path = '') {
  const images = []
  
  if (!obj || typeof obj !== 'object') {
    return images
  }
  
  // Check if this is an image node
  if (obj.type === 'image' || obj.type === 'imageWithTitle') {
    images.push({ node: obj, path })
  }
  
  // Recursively search in arrays and objects
  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      images.push(...findAllImageNodes(item, `${path}[${index}]`))
    })
  } else {
    Object.keys(obj).forEach(key => {
      if (key !== 'parent' && key !== 'marks') { // Skip circular references
        images.push(...findAllImageNodes(obj[key], path ? `${path}.${key}` : key))
      }
    })
  }
  
  return images
}

export function renderTiptapHTML(contentJSON) {
  if (!contentJSON) {
    return ''
  }

  // If contentJSON is a string, try to parse it
  let parsedContent = contentJSON
  if (typeof contentJSON === 'string') {
    try {
      parsedContent = JSON.parse(contentJSON)
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ Failed to parse contentJSON as JSON:', e)
      }
      return ''
    }
  }

  // If parsedContent has a 'json' property, use that
  if (parsedContent && typeof parsedContent === 'object' && parsedContent.json) {
    parsedContent = parsedContent.json
    // If it's still a string, parse it again
    if (typeof parsedContent === 'string') {
      try {
        parsedContent = JSON.parse(parsedContent)
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('⚠️ Failed to parse contentJSON.json as JSON:', e)
        }
        return ''
      }
    }
  }

  // Handle TipTap document structure
  if (parsedContent && typeof parsedContent === 'object') {
    // If it's a document with content array
    if (parsedContent.type === 'doc' && Array.isArray(parsedContent.content)) {
      const html = parsedContent.content.map(node => convertNodeToHTML(node)).join('')
      return html
    }
    // If it's just a content array
    if (Array.isArray(parsedContent.content)) {
      const html = parsedContent.content.map(node => convertNodeToHTML(node)).join('')
      return html
    }
    // If it's an array of nodes
    if (Array.isArray(parsedContent)) {
      const html = parsedContent.map(node => convertNodeToHTML(node)).join('')
      return html
    }
    // If it's a single node
    if (parsedContent.type) {
      return convertNodeToHTML(parsedContent)
    }
  }

  if (process.env.NODE_ENV === 'development') {
    console.warn('⚠️ Content is not in expected TipTap format:', parsedContent)
  }
  return ''
}

export default function BlogPost({ post }) {
  if (!post || !post.content) {
    return null
  }

  const html = renderTiptapHTML(post.content)
  
  return (
    <div
      className="prose prose-lg mx-auto"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}


